import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { useAuth } from '../auth/AuthContext';
import { useSettings } from '../settings/SettingsContext';
import { useSpeech } from '../hooks/useSpeech';
import { Loading } from '../components/Loading';
import Crumbs from '../components/Crumbs';
import FlashcardConfigPanel from '../study/FlashcardConfigPanel';
import type { Language } from '../../../shared/types';

export default function ProfilePage() {
  const api = useApi();
  const { user } = useAuth();
  const { settings, update, saveError } = useSettings();

  const languages = useAsync(() => catalogApi.languages(api), [api]);
  const stats = useAsync(() => progressApi.stats(api), [api]);

  const active: Language | null =
    languages.data?.languages.find((l) => l.code === settings.activeLanguage) ??
    languages.data?.languages[0] ??
    null;

  const speech = useSpeech(active?.speechTag ?? '');
  const audioUnavailable = !speech.supported || !speech.voiceAvailable;

  return (
    <main className="page">
      <Crumbs items={[{ label: 'Languages', to: '/' }, { label: 'Profile' }]} />

      <header className="page-header">
        <h1>Profile &amp; card setup</h1>
        <p className="muted">
          {user?.displayName} · {user?.email}
        </p>
      </header>

      {stats.data && (
        <section className="stat-row">
          <div className="stat-tile">
            <span className="stat-value">{stats.data.currentStreak}</span>
            <span className="stat-label">day streak</span>
          </div>
          <div className="stat-tile">
            <span className="stat-value">{stats.data.longestStreak}</span>
            <span className="stat-label">longest streak</span>
          </div>
          <div className="stat-tile">
            <span className="stat-value">{stats.data.totalCardsReviewed}</span>
            <span className="stat-label">cards reviewed</span>
          </div>
          <div className="stat-tile">
            <span className="stat-value">{stats.data.cardsKnown}</span>
            <span className="stat-label">words known</span>
          </div>
        </section>
      )}

      {stats.data?.lastStudyDate && (
        <p className="muted footnote">Last studied {stats.data.lastStudyDate}.</p>
      )}

      <div className="section-head">
        <h2>Flashcards</h2>
      </div>
      <p className="muted">
        This setup is used everywhere, and saves as you change it. You can also adjust it from any
        section's exercise tab, just before you start.
      </p>

      {saveError && (
        <p className="error">
          {saveError} — your changes are applied here but were not saved to your account.
        </p>
      )}

      {languages.loading && <Loading what="your settings" />}

      {active && (
        <>
          {languages.data && languages.data.languages.length > 1 && (
            <label className="field field--inline">
              Configure for
              <select
                value={active.code}
                onChange={(e) => update({ activeLanguage: e.target.value })}
              >
                {languages.data.languages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <FlashcardConfigPanel
            config={settings.flashcards}
            language={active}
            audioUnavailable={audioUnavailable}
            voiceName={speech.voiceName}
            onChange={(flashcards) => update({ flashcards })}
            sessionSize={settings.sessionSize}
            onSessionSizeChange={(sessionSize) => update({ sessionSize })}
            // No single section in view here, so the sitting choices are shown
            // without per-deck counts. See sittingChoices().
            deckSize={0}
          />

          {speech.supported && !speech.voiceAvailable && (
            <p className="callout callout--quiet">
              No {active.name} voice is installed on this device, so audio is unavailable here.
              Cards are spoken by your operating system's voices rather than by recordings, so
              installing a {active.name} voice in your system language settings turns this on.
            </p>
          )}
        </>
      )}
    </main>
  );
}
